/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */

import apiClient from './client';
import {
  TollDistributionSettingDTO,
  CreateTollDistributionSettingDTO,
  UpdateTollDistributionSettingDTO,
  TollDistributionCalculationRequest,
  TollDistributionCalculationResponse,
  VehicleInfo,
  ConnectedAccountInfo,
  TollDistributionSummary,
  TollDistributionValidationResponse,
} from '@/types';

const BASE_PATH = '/toll-distribution';

export const tollDistributionApi = {
  // Get all settings for current user
  getSettings: async (): Promise<TollDistributionSettingDTO[]> => {
    const response = await apiClient.get<TollDistributionSettingDTO[]>(`${BASE_PATH}/settings`);
    return response.data;
  },

  // Get specific setting by ID
  getSettingById: async (settingId: string): Promise<TollDistributionSettingDTO> => {
    const response = await apiClient.get<TollDistributionSettingDTO>(`${BASE_PATH}/settings/${settingId}`);
    return response.data;
  },

  // Create new setting
  createSetting: async (dto: CreateTollDistributionSettingDTO): Promise<TollDistributionSettingDTO> => {
    const response = await apiClient.post<TollDistributionSettingDTO>(`${BASE_PATH}/settings`, dto);
    return response.data;
  },

  // Update existing setting
  updateSetting: async (settingId: string, dto: UpdateTollDistributionSettingDTO): Promise<TollDistributionSettingDTO> => {
    const response = await apiClient.put<TollDistributionSettingDTO>(`${BASE_PATH}/settings/${settingId}`, dto);
    return response.data;
  },

  // Delete setting
  deleteSetting: async (settingId: string): Promise<void> => {
    await apiClient.delete(`${BASE_PATH}/settings/${settingId}`);
  },

  // Get effective setting for vehicle + toll system
  getEffectiveSetting: async (vehicleId?: string, tollSystemName?: string): Promise<TollDistributionSettingDTO> => {
    const params: Record<string, string> = {};
    if (vehicleId) params.vehicleId = vehicleId;
    if (tollSystemName) params.tollSystemName = tollSystemName;
    const response = await apiClient.get<TollDistributionSettingDTO>(`${BASE_PATH}/settings/effective`, { params });
    return response.data;
  },

  // Calculate distribution preview
  calculateDistribution: async (request: TollDistributionCalculationRequest): Promise<TollDistributionCalculationResponse> => {
    const response = await apiClient.post<TollDistributionCalculationResponse>(`${BASE_PATH}/calculate`, request);
    return response.data;
  },

  // Get user vehicles for dropdown
  getVehicles: async (): Promise<VehicleInfo[]> => {
    const response = await apiClient.get<VehicleInfo[]>(`${BASE_PATH}/vehicles`);
    return response.data;
  },

  // Get user connected Stripe accounts
  getConnectedAccounts: async (): Promise<ConnectedAccountInfo[]> => {
    const response = await apiClient.get<ConnectedAccountInfo[]>(`${BASE_PATH}/connected-accounts`);
    return response.data;
  },

  // Validate setting before create
  validateSetting: async (dto: CreateTollDistributionSettingDTO): Promise<TollDistributionValidationResponse> => {
    const response = await apiClient.post<TollDistributionValidationResponse>(`${BASE_PATH}/settings/validate`, dto);
    return response.data;
  },

  // Get summary for dashboard
  getSummary: async (): Promise<TollDistributionSummary> => {
    const response = await apiClient.get<TollDistributionSummary>(`${BASE_PATH}/summary`);
    return response.data;
  },

  // Check if setting already exists
  checkExists: async (vehicleId?: string, tollSystemName?: string): Promise<boolean> => {
    const params: Record<string, string> = {};
    if (vehicleId) params.vehicleId = vehicleId;
    if (tollSystemName) params.tollSystemName = tollSystemName;
    const response = await apiClient.get<boolean>(`${BASE_PATH}/settings/exists`, { params });
    return response.data;
  },
};
